<script>
import countries from 'shared/constants/countries.js';
import parsePhoneNumber from 'libphonenumber-js';
import {
  getActiveCountryCode,
  getActiveDialCode,
} from 'shared/components/PhoneInput/helper';
import WithLabel from './WithLabel.vue';

export default {
  components: { WithLabel },
  inheritAttrs: false,
  props: {
    modelValue: {
      type: [String, Number],
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    styles: {
      type: Object,
      default: () => {},
    },
    label: {
      type: String,
      required: true,
    },
    icon: {
      type: String,
      default: '',
    },
    name: {
      type: String,
      required: true,
    },
    hasError: Boolean,
    errorMessage: {
      type: String,
      default: '',
    },
    isFirstItem: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    autocomplete: {
      type: String,
      default: 'tel',
    },
  },
  emits: ['blur', 'setCode', 'update:modelValue'],
  data() {
    return {
      selectedIndex: -1,
      showDropdown: false,
      searchCountry: '',
      activeCountryCode: getActiveCountryCode(),
      activeDialCode: getActiveDialCode(),
      phoneNumber: this.modelValue,
      uniqueId: `phone-${Date.now()}-${Math.random().toString(36).substring(7)}`,
    };
  },
  computed: {
    countries() {
      return [
        {
          name: this.dropdownFirstItemName,
          dial_code: '',
          emoji: '',
          id: '',
        },
        ...countries,
      ];
    },
    dropdownFirstItemName() {
      return this.activeCountryCode ? 'Clear selection' : 'Select Country';
    },
    filteredCountriesBySearch() {
      return this.countries.filter(country => {
        const { name, dial_code, id } = country;
        const search = this.searchCountry.toLowerCase();
        return (
          name.toLowerCase().includes(search) ||
          dial_code.toLowerCase().includes(search) ||
          id.toLowerCase().includes(search)
        );
      });
    },
    activeCountry() {
      if (this.activeCountryCode) {
        return this.countries.find(
          country => country.id === this.activeCountryCode
        );
      }
      return '';
    },
  },
  watch: {
    modelValue() {
      const number = parsePhoneNumber(this.modelValue);
      if (number) {
        this.activeCountryCode = number.country;
        this.activeDialCode = `+${number.countryCallingCode}`;
        this.phoneNumber = this.modelValue.replace(
          `+${number.countryCallingCode}`,
          ''
        );
      } else {
        this.phoneNumber = this.modelValue;
      }
    },
  },
  mounted() {
    this.setActiveCountry();
    document.addEventListener('click', this.onOutsideClick);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.onOutsideClick);
  },
  methods: {
    onOutsideClick(e) {
      const wrap = this.$el;
      if (this.showDropdown && wrap && !wrap.contains(e.target)) {
        this.closeDropdown();
      }
    },
    onChange(e) {
      this.phoneNumber = e.target.value;
      this.$emit('update:modelValue', e.target.value);
      this.$emit('setCode', this.activeDialCode);
    },
    onBlur(e) {
      this.$emit('blur', e.target.value);
    },
    onSearchCountry() {
      this.selectedIndex = 0;
    },
    moveUp() {
      if (!this.showDropdown) return;
      this.selectedIndex = Math.max(this.selectedIndex - 1, 0);
      this.scrollToSelected();
    },
    moveDown() {
      if (!this.showDropdown) return;
      this.selectedIndex = Math.min(
        this.selectedIndex + 1,
        this.filteredCountriesBySearch.length - 1
      );
      this.scrollToSelected();
    },
    scrollToSelected() {
      this.$nextTick(() => {
        const dropdown = this.$refs.dropdown;
        const selectedItem = this.$refs.dropdownItem?.[this.selectedIndex];
        const dropdownSearchbarHeight = 40;
        if (dropdown && selectedItem) {
          dropdown.scrollTop = selectedItem.offsetTop - dropdownSearchbarHeight;
        }
      });
    },
    onSelectCountry(country) {
      if (!country || !this.showDropdown) return;
      this.activeCountryCode = country.id;
      this.searchCountry = '';
      this.activeDialCode = country.dial_code;
      this.$emit('setCode', country.dial_code);
      this.closeDropdown();
      this.$refs.phoneNumberInput?.focus();
    },
    setActiveCountry() {
      const { phoneNumber } = this;
      if (!phoneNumber) return;
      const number = parsePhoneNumber(phoneNumber);
      if (number) {
        this.activeCountryCode = number.country;
        this.activeDialCode = `+${number.countryCallingCode}`;
      }
    },
    toggleCountryDropdown() {
      this.showDropdown = !this.showDropdown;
      this.selectedIndex = -1;
      if (this.showDropdown) {
        this.$nextTick(() => {
          this.$refs.searchbar?.focus();
        });
      }
    },
    closeDropdown() {
      this.selectedIndex = -1;
      this.showDropdown = false;
    },
  },
};
</script>

<template>
  <WithLabel
    :label="label"
    :icon="icon"
    :name="name"
    :required="required"
    :has-error="hasError"
    :error-message="errorMessage"
    :class="{ 'mt-4': !isFirstItem }"
  >
    <template #rightOfLabel>
      <slot />
    </template>

    <div class="relative phone-input--wrap w-full !mt-0">
      <div
        class="flex items-center justify-start border border-solid min-[1440px]:rounded-xl rounded-lg !bg-transparent dark:!bg-transparent !h-12"
        style="flex-direction: row-reverse"
        :class="
          hasError
            ? 'border border-solid border-red-400 dark:border-red-400 mb-0'
            : '!border-[#BDBDBD] dark:border-slate-600 focus-within:border-[#3B9EFF]'
        "
      >
        <div
          class="cursor-pointer py-2 pr-1.5 pl-2 flex items-center justify-center gap-1.5 !h-12 min-[1440px]:!h-14 w-14"
          style="flex-direction: row-reverse"
          @click.prevent="toggleCountryDropdown"
        >
          <h5 v-if="activeCountry" class="mb-0">
            {{ activeCountry.emoji }}
          </h5>
          <fluent-icon v-else icon="globe" class="fluent-icon" size="16" />
          <fluent-icon icon="chevron-down" class="fluent-icon" size="12" />
        </div>

        <div
          class="mx-1 h-6"
          :class="
            hasError
              ? 'border-l border-solid border-red-400 dark:border-red-400'
              : 'border-slate-300 border-l dark:border-white'
          "
        />

        <span
          v-if="activeDialCode"
          class="flex mr-2 py-2 pl-2 pr-0 text-base font-normal leading-normal bg-transparent dark:bg-transparent"
          :class="{
            'text-red-400': hasError,
            'text-slate-400 dark:text-slate-300': !phoneNumber && !hasError,
            'text-slate-900 dark:text-white': phoneNumber && !hasError,
          }"
          style="direction: ltr"
        >
          {{ activeDialCode }}
        </span>

        <input
          :id="uniqueId"
          ref="phoneNumberInput"
          v-bind="$attrs"
          :name="name"
          :value="phoneNumber"
          type="tel"
          inputmode="tel"
          :autocomplete="autocomplete"
          class="!rounded-tl-none !rounded-bl-none !border-0 !outline-none font-normal !w-full !bg-transparent dark:!bg-transparent text-slate-900 dark:text-slate-100 text-base !px-1.5 placeholder:font-normal placeholder:text-[#9A9AA2] dark:placeholder:text-slate-400 placeholder:text-right !mb-0"
          :placeholder="placeholder"
          :readonly="readonly"
          :style="styles"
          @input="onChange"
          @blur="onBlur"
        />
      </div>

      <div
        v-if="showDropdown"
        ref="dropdown"
        tabindex="0"
        class="z-10 absolute h-60 w-[12.5rem] shadow-md overflow-y-auto top-10 left-0 rounded px-0 pt-0 pb-1 bg-white dark:!bg-slate-900"
        @keydown.prevent.up="moveUp"
        @keydown.prevent.down="moveDown"
        @keydown.prevent.enter="
          onSelectCountry(filteredCountriesBySearch[selectedIndex])
        "
      >
        <div class="sticky top-0 p-1 bg-white dark:bg-slate-900">
          <input
            ref="searchbar"
            v-model="searchCountry"
            type="text"
            :placeholder="$t('GENERAL.PHONE_INPUT.PLACEHOLDER')"
            class="!h-8 !mb-0 !text-sm !border !border-solid !border-slate-2000 dark:!border-slate-6000"
            @input="onSearchCountry"
          />
        </div>

        <div
          v-for="(country, index) in filteredCountriesBySearch"
          ref="dropdownItem"
          :key="index"
          class="flex items-center px-1 py-0 cursor-pointer h-7 hover:bg-slate-50 dark:hover:bg-slate-700"
          :class="{
            'bg-slate-50 dark:bg-slate-700': country.id === activeCountryCode,
            'bg-slate-25 dark:bg-slate-800': index === selectedIndex,
          }"
          @click="onSelectCountry(country)"
        >
          <span class="mr-1 text-base">{{ country.emoji }}</span>

          <span
            class="max-w-[7.5rem] overflow-hidden text-ellipsis whitespace-nowrap"
          >
            {{ country.name }}
          </span>

          <span class="ml-1 text-xs text-slate-300 dark:text-slate-300">
            {{ country.dial_code }}
          </span>
        </div>

        <div v-if="filteredCountriesBySearch.length === 0">
          <span
            class="flex items-center justify-center mt-4 text-sm text-slate-500 dark:text-slate-300"
          >
            {{ $t('GENERAL.PHONE_INPUT.EMPTY_STATE') }}
          </span>
        </div>
      </div>
    </div>
  </WithLabel>
</template>

<style scoped>
input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus,
input:-webkit-autofill:active {
  -webkit-box-shadow: 0 0 0 30px transparent inset !important;
  background-color: transparent !important;
  -webkit-text-fill-color: currentColor !important;
  transition: background-color 9999s ease-out 0s;
}
</style>

<style>
/* Dark mode autofill text color - unscoped to target html/body dark class */
.dark input:-webkit-autofill,
.dark input:-webkit-autofill:hover,
.dark input:-webkit-autofill:focus,
.dark input:-webkit-autofill:active {
  -webkit-text-fill-color: rgb(241 245 249) !important; /* slate-100 */
}

@media (prefers-color-scheme: dark) {
  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus,
  input:-webkit-autofill:active {
    -webkit-text-fill-color: rgb(241 245 249) !important; /* slate-100 */
  }
}
</style>
